/**
 * Retenues sur le CA des abonnements — Sacem, commissions… — saisies ici.
 *
 * Une ligne = un libellé, un pourcentage, une base (CA TTC ou CA HT). Chaque
 * retenue devient une tuile des Sales KPIs et se retranche du « Final HT ».
 * Rien n'est écrit en dur : ajouter « Distributeur 1 % du CA TTC » ne demande
 * aucun changement de code.
 */

import { DEDUCTION_LABEL_MAX_LENGTH, type DeductionBase, type SubscriptionDeduction, type SubscriptionDeductionInput } from '@shared/types/settings';
import { useEffect, useState, type FormEvent } from 'react';

import { useDeductions, useWriteDeduction } from '../hooks/useDeductions';
import { Card } from './Card';

export const DEDUCTION_BASE_LABELS: Record<DeductionBase, string> = { ttc: 'du CA TTC', ht: 'du CA HT' };

interface Draft {
  label: string;
  percent: string;
  base: DeductionBase;
}

const toDraft = (deduction?: SubscriptionDeduction): Draft =>
  deduction
    ? { label: deduction.label, percent: String(deduction.percent).replace('.', ','), base: deduction.base }
    : { label: '', percent: '', base: 'ttc' };

/** « 8,5 » → 8.5 ; `null` si illisible ou hors 0–100. */
function parsePercent(raw: string): number | null {
  const cleaned = raw.replace(/\s/g, '').replace('%', '').replace(',', '.');
  if (cleaned === '') return null;
  const value = Number(cleaned);
  return Number.isFinite(value) && value >= 0 && value <= 100 ? value : null;
}

function toInput(draft: Draft): SubscriptionDeductionInput | string {
  const percent = parsePercent(draft.percent);
  if (!draft.label.trim()) return 'Libellé requis.';
  if (percent === null) return 'Pourcentage : un nombre entre 0 et 100.';
  return { label: draft.label.trim(), percent, base: draft.base };
}

function DeductionRow({
  deduction,
  isSaving,
  onSave,
  onDelete,
  submitLabel,
}: {
  deduction?: SubscriptionDeduction;
  isSaving: boolean;
  onSave: (input: SubscriptionDeductionInput) => Promise<void>;
  onDelete?: () => Promise<void>;
  submitLabel: string;
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(deduction));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraft(toDraft(deduction)), [deduction]);

  const idPrefix = `deduction-${deduction?.id ?? 'new'}`;
  const dirty = !deduction || JSON.stringify(draft) !== JSON.stringify(toDraft(deduction));

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const input = toInput(draft);
    if (typeof input === 'string') return setError(input);
    setError(null);
    try {
      await onSave(input);
      if (!deduction) setDraft(toDraft());
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Enregistrement impossible.');
    }
  };

  return (
    <form className="marker-form deduction-row" onSubmit={(event) => void handleSubmit(event)}>
      <div className="field marker-form__grow">
        <label className="field__label" htmlFor={`${idPrefix}-label`}>Libellé</label>
        <input
          id={`${idPrefix}-label`}
          type="text"
          maxLength={DEDUCTION_LABEL_MAX_LENGTH}
          value={draft.label}
          onChange={(event) => setDraft((current) => ({ ...current, label: event.target.value }))}
          placeholder="ex. Sacem"
        />
      </div>
      <div className="field">
        <label className="field__label" htmlFor={`${idPrefix}-percent`}>%</label>
        <input
          id={`${idPrefix}-percent`}
          type="text"
          inputMode="decimal"
          value={draft.percent}
          onChange={(event) => setDraft((current) => ({ ...current, percent: event.target.value }))}
          style={{ width: 72 }}
          placeholder="8"
        />
      </div>
      <div className="field">
        <label className="field__label" htmlFor={`${idPrefix}-base`}>Base</label>
        <select
          id={`${idPrefix}-base`}
          value={draft.base}
          onChange={(event) => setDraft((current) => ({ ...current, base: event.target.value as DeductionBase }))}
        >
          <option value="ttc">{DEDUCTION_BASE_LABELS.ttc}</option>
          <option value="ht">{DEDUCTION_BASE_LABELS.ht}</option>
        </select>
      </div>
      <div className="marker-form__actions">
        <button type="submit" className="btn btn--primary" disabled={isSaving || !dirty}>
          {submitLabel}
        </button>
        {onDelete && (
          <button type="button" className="btn" disabled={isSaving} onClick={() => void onDelete()}>
            Retirer
          </button>
        )}
      </div>
      {error && (
        <p className="login__error marker-form__error" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

export function DeductionsEditor() {
  const deductions = useDeductions();
  const write = useWriteDeduction();
  const isSaving = write.isPending;

  return (
    <Card
      title="Retenues sur le CA des abonnements"
      subtitle="Sacem, Fees Stripe, commissions… : chacune devient une tuile de la page Souscriptions et se déduit du Final HT (et de la MC2 avec abo). Réglage commun à tous les admins."
      className="marker-form-card"
    >
      {deductions.isError && (
        <p className="notice notice--error" role="alert">
          Retenues illisibles : {deductions.error.message}
        </p>
      )}
      {(deductions.data ?? []).map((deduction) => (
        <DeductionRow
          key={deduction.id}
          deduction={deduction}
          isSaving={isSaving}
          submitLabel="Enregistrer"
          onSave={async (input) => {
            await write.mutateAsync({ kind: 'update', id: deduction.id, input });
          }}
          onDelete={async () => {
            if (!window.confirm(`Retirer la retenue « ${deduction.label} » ?`)) return;
            await write.mutateAsync({ kind: 'delete', id: deduction.id });
          }}
        />
      ))}
      <DeductionRow
        isSaving={isSaving}
        submitLabel="Ajouter"
        onSave={async (input) => {
          await write.mutateAsync({ kind: 'create', input });
        }}
      />
    </Card>
  );
}
